// Периоды работы из резюме: «сколько там проработал».
//
// Жило внутри ClientView (страница для клиента); вынесено, потому что тот же
// стаж нужен плашке ЛПР в ленте кандидата («март 2022 — июнь 2026 · 4 года
// 4 мес»). Держать подсчёт месяцев в двух копиях значило бы однажды получить
// у одного и того же места работы два разных стажа.
//
// Даты hh и расширения — «YYYY-MM-01»; ручные и разобранные из файла резюме
// хранят свободный текст «как в резюме», и тогда показываем его как есть.

/** Русское склонение по числу: 1 год, 2 года, 5 лет. */
export function plural(n: number, one: string, few: string, many: string): string {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m100 >= 11 && m100 <= 14) return many;
  if (m10 === 1) return one;
  if (m10 >= 2 && m10 <= 4) return few;
  return many;
}

const YM = /^(\d{4})-(\d{2})/;

/** Сколько месяцев от начала до конца включительно — как считает hh: «март —
 *  июнь» это четыре месяца, а не три. Нет конца — по сегодняшний месяц. */
function monthsBetween(
  start: RegExpExecArray,
  end: RegExpExecArray | null,
  now: Date,
): number {
  const ey = end ? Number(end[1]) : now.getFullYear();
  const em = end ? Number(end[2]) : now.getMonth() + 1;
  return Math.max(0, (ey - Number(start[1])) * 12 + (em - Number(start[2])) + 1);
}

// "2017-05-01 — 2025-12-01" / "2021-03-01 — н.в." → «2017 —», «2025», «8 л 7 мес».
export function splitPeriod(
  period: string,
  now: Date = new Date(),
): { from: string; to: string; dur: string | null } {
  const [a, b] = period.split(" — ");
  const start = YM.exec(a ?? "");
  const end = YM.exec(b ?? "");
  if (!start) return { from: period, to: "", dur: null };
  const months = monthsBetween(start, end, now);
  const y = Math.floor(months / 12);
  const m = months % 12;
  const dur =
    y > 0 && m > 0 ? `${y} г ${m} мес` : y > 0 ? `${y} ${plural(y, "год", "года", "лет")}` : `${m} мес`;
  return { from: `${start[1]} —`, to: end ? end[1] : "наст. время", dur };
}

const MONTHS_NOM = [
  "январь",
  "февраль",
  "март",
  "апрель",
  "май",
  "июнь",
  "июль",
  "август",
  "сентябрь",
  "октябрь",
  "ноябрь",
  "декабрь",
];

const monthYear = (d: RegExpExecArray) => `${MONTHS_NOM[Number(d[2]) - 1] ?? d[2]} ${d[1]}`;

/** «март 2022 — июнь 2026 · 4 года 4 мес» из сырых start/end резюме. Нет
 *  конца — «по наст. время». Начало не дата (резюме набрано руками) — null:
 *  тогда показывают исходную строку периода, придумывать по ней нечего. */
export function formatWorkPeriod(
  start: string | null | undefined,
  end: string | null | undefined,
  now: Date = new Date(),
): string | null {
  const s = YM.exec(start ?? "");
  if (!s) return null;
  const e = YM.exec(end ?? "");
  const months = monthsBetween(s, e, now);
  const y = Math.floor(months / 12);
  const m = months % 12;
  const dur = [y > 0 ? `${y} ${plural(y, "год", "года", "лет")}` : "", m > 0 ? `${m} мес` : ""]
    .filter(Boolean)
    .join(" ");
  const range = `${monthYear(s)} — ${e ? monthYear(e) : "по наст. время"}`;
  return dur ? `${range} · ${dur}` : range;
}
